import {
  Box,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useSearchParams } from 'react-router';
import { MapboxMapWithMarkers } from '../../possibly-stateful-components/Map/MapboxMapWithMarkers';
import { ResultsList } from './possibly-stateful-components/results-list/results-list';
import { useState, useMemo, useEffect, useCallback } from 'react';
import VrpanoRoundedIcon from '@mui/icons-material/VrpanoRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import { SearchFilters } from './components/search-filters/search-filters.tsx';
import {
  buildRequest,
  filterSearchResults,
  sortLandSearch,
} from '../../utils/search.ts';
import { AccountRequiredModal } from '../../components/account-required-modal/account-required-modal.tsx';
import './search-page.scss';
import { MapboxMap } from '../../possibly-stateful-components/Map/MapboxMap';
import { useGetLandSearch } from '../../hooks/search.ts';
import { useLatLon } from './hooks/location.ts';
import { useTitle } from '../../hooks/useTitle.ts';
import {
  defaultSearchParams,
  SearchParamsOutput,
  SearchParamsSchema,
  SortOrder,
  LotStatus,
} from '../../constants/search.ts';
import { safeParse } from 'valibot';
import { useGetFavorites } from '../../api/favorites.ts';
import { useGetCurrentUser } from '../../hooks/auth.ts';
import { useAddSavedSearch } from '../../hooks/saved-search.ts';

export const SearchPage = () => {
  useTitle('Find Your Land - Immersive Homes');

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const urlParamsObj = useMemo(
    () =>
      Object.fromEntries(
        urlSearchParams
      ) as never as Partial<SearchParamsOutput>,
    [urlSearchParams]
  );

  const { output: params } = useMemo(
    () =>
      safeParse(SearchParamsSchema, {
        ...defaultSearchParams,
        ...urlParamsObj,
      }) as { output: SearchParamsOutput },
    [urlParamsObj]
  );

  const searchParams = useMemo(() => {
    return buildRequest(params);
  }, [params]);

  const {
    data: landSearchResponse,
    loading: landSearchLoading,
    error: landSearchError,
    isOverLimit: isOverSearchLimit,
    fetchNextPage,
  } = useGetLandSearch({
    query: searchParams,
    setPage: (page) => {
      setUrlSearchParams((localParams) => {
        localParams.set('page', page.toString());
        return localParams;
      });
    },
    initialPage: params.page,
  });

  const { data: user } = useGetCurrentUser();
  const { data: favoritesData } = useGetFavorites(user?.userId ?? '');

  const results = useMemo(() => {
    if (isOverSearchLimit) return [];
    if (!landSearchResponse) return landSearchResponse;

    const sorted = sortLandSearch({
      data: landSearchResponse?.data,
      favoritesData,
      queryParams: params,
    });

    return filterSearchResults({
      data: sorted,
      params,
    });
  }, [isOverSearchLimit, landSearchResponse, favoritesData, params]);

  const { data, isPending } = useLatLon(params);
  const { latitude, longitude } = data ?? {};

  const [view, setView] = useState('list');

  const isMobile = useMediaQuery('(max-width:900px)');

  const [isAccountRequiredModalOpen, setIsAccountRequiredModalOpen] =
    useState(isOverSearchLimit);

  // TODO: Not a great usage of useEffect, let's axe it
  useEffect(() => {
    if (isOverSearchLimit) {
      setIsAccountRequiredModalOpen(true);
    }
  }, [isOverSearchLimit]);

  const setUrlParam = useCallback(
    <TKey extends keyof typeof params>(
      key: TKey,
      val: (typeof params)[TKey] | undefined,
      transform: (val: Exclude<(typeof params)[TKey], undefined>) => string
    ) => {
      setUrlSearchParams((localParams) => {
        if (val === undefined) {
          localParams.delete(key);
        } else {
          localParams.set(key, transform(val as never));
        }
        return localParams;
      });
    },
    [setUrlSearchParams]
  );

  const setMaxPrice = useCallback(
    (maxPrice: number | undefined) => {
      setUrlParam('maxPrice', maxPrice, (v) => String(v));
    },
    [setUrlParam]
  );

  const setMinPrice = useCallback(
    (minPrice: number | undefined) => {
      setUrlParam('minPrice', minPrice, (v) => String(v));
    },
    [setUrlParam]
  );

  const setMinSize = useCallback(
    (minSize: number | undefined) => {
      setUrlParam('minSize', minSize, (v) => String(v));
    },
    [setUrlParam]
  );

  const setMaxSize = useCallback(
    (maxSize: number | undefined) => {
      setUrlParam('maxSize', maxSize, (v) => String(v));
    },
    [setUrlParam]
  );

  const setIncludeNonVacantLots = useCallback(
    (includeNonVacantLots: boolean | undefined) => {
      setUrlParam('includeNonVacantLots', includeNonVacantLots, (v) =>
        String(v)
      );
    },
    [setUrlParam]
  );

  const setSelectedSortOrder = useCallback(
    (sort: SortOrder | undefined) => {
      setUrlParam('sort', sort, (v) => v);
    },
    [setUrlParam]
  );

  const setLotStatus = useCallback(
    (lotStatus: LotStatus | undefined) => {
      setUrlParam('lotStatus', lotStatus, (v) => v);
      // TODO: Add to default params as well so that if `Sold` is selected, it will default to `Recently Updated` on initial load as well
      if (lotStatus === 'Sold') {
        setSelectedSortOrder('Recently Updated');
      }
    },
    [setSelectedSortOrder, setUrlParam]
  );

  const builtRequest = useMemo(() => {
    return buildRequest(params);
  }, [params]);

  const { mutate: addSavedSearchMutate, isPending: isSavedSearchPending } =
    useAddSavedSearch(user?.userId || '', builtRequest);

  const [isSaveSearchModalOpen, setIsSaveSearchModalOpen] = useState(false);

  const addSavedSearch = (val: string) => {
    addSavedSearchMutate(val, {
      onSuccess: () => {
        setIsSaveSearchModalOpen(false);
      },
    });
  };

  const closeSaveSearchModal = useCallback(() => {
    setIsSaveSearchModalOpen(false);
  }, []);

  const openSaveSearchModal = useCallback(() => {
    setIsSaveSearchModalOpen(false);
  }, []);

  return (
    <Box
      data-testid="search-page"
      sx={{
        flex: 1,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SearchFilters
        maxPrice={params.maxPrice}
        setMaxPrice={setMaxPrice}
        minPrice={params.minPrice}
        setMinPrice={setMinPrice}
        minSize={params.minSize}
        setMinSize={setMinSize}
        maxSize={params.maxSize}
        setMaxSize={setMaxSize}
        includeNonVacantLots={params.includeNonVacantLots}
        setIncludeNonVacantLots={setIncludeNonVacantLots}
        lotStatus={params.lotStatus}
        setLotStatus={setLotStatus}
        setSelectedSortOrder={setSelectedSortOrder}
        selectedSortOrder={params.sort}
        isSavedSearchPending={isSavedSearchPending}
        isSaveSearchModalOpen={isSaveSearchModalOpen}
        addSavedSearch={addSavedSearch}
        closeSaveSearchModal={closeSaveSearchModal}
        openSaveSearchModal={openSaveSearchModal}
        user={user}
      />
      <Grid container className="grid-container">
        {params && params['state'] !== undefined ? (
          <>
            {(!isMobile || view === 'list') && (
              <Grid
                data-testid="results-list-component"
                sx={{ maxHeight: '100%', overflow: 'auto' }}
                className="results-list-component"
                size={{
                  sm: 12,
                  md: 6,
                }}
              >
                {/* <Box>Search Count: {searchCount}</Box> */}
                {!isOverSearchLimit ? (
                  <>
                    <ResultsList
                      results={results}
                      count={landSearchResponse?.count}
                      loading={landSearchLoading}
                      error={landSearchError}
                      loadMore={() => fetchNextPage()}
                    />
                  </>
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Box>Please sign in to continue searching</Box>
                  </Box>
                )}
              </Grid>
            )}
            {(!isMobile || view === 'map') && (
              <Grid
                className="map-grid"
                data-testid="mapbox-map"
                size={{
                  xs: 12,
                  sm: 12,
                  md: 6,
                }}
              >
                {isOverSearchLimit ? (
                  <MapboxMapWithMarkers
                    results={[]}
                    isLoading={landSearchLoading}
                  />
                ) : landSearchError === null &&
                  (landSearchResponse?.count ?? 0) > 0 ? (
                  <MapboxMapWithMarkers
                    results={results}
                    isLoading={landSearchLoading}
                  />
                ) : (
                  <MapboxMap
                    latitude={latitude}
                    longitude={longitude}
                    isPending={isPending}
                  />
                )}
              </Grid>
            )}
          </>
        ) : (
          <Box className="no-results" data-testid="no-results">
            <Typography
              className="no-results-text"
              data-testid="no-results-text"
            >
              Please search for a property
            </Typography>
          </Box>
        )}
      </Grid>
      {isMobile && (
        <ToggleButtonGroup
          data-testid="toggle-button-group"
          sx={{
            position: 'absolute',
            bottom: '40px',
            right: '32px',
            background: '#002D56',
            border: 'unset',
            borderRadius: '0px',
            // TODO: Why this number??
            zIndex: 1030,
          }}
          color="primary"
          value={view}
          exclusive
          onChange={(_e, value: string) => setView(value)}
          aria-label="toggle-view"
        >
          <ToggleButton
            value="list"
            sx={{
              border: 'none',
              color: 'white',
              display: view === 'map' ? 'flex' : 'none',
            }}
            size="small"
          >
            <ViewListRoundedIcon sx={{ marginRight: '8px' }} /> List
          </ToggleButton>
          <ToggleButton
            value="map"
            sx={{
              border: 'none',
              color: 'white',
              display: view === 'list' ? 'flex' : 'none',
            }}
            size="small"
          >
            <VrpanoRoundedIcon sx={{ marginRight: '8px' }} /> Map
          </ToggleButton>
        </ToggleButtonGroup>
      )}
      {isOverSearchLimit && (
        <AccountRequiredModal
          open={isAccountRequiredModalOpen}
          onClose={() => setIsAccountRequiredModalOpen(false)}
        />
      )}
    </Box>
  );
};
